import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Configuration, ThemeConfig } from '@nylas/core';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';

const defaultRoundToMinutes = 15;
/**
 * The `nylas-timeslot-interval` component allows users to set their time slot interval preferences.
 * This component provides an option to set both `interval_minutes` and `round_to` in the configuration.
 *
 * @part nti - The timeslot interval container
 * @part nti__header - The header of the timeslot interval picker
 * @part nti__input-label - The input label of the timeslot interval picker
 * @part nti__dropdown - The dropdown container
 * @part nti__dropdown-button - The dropdown button
 * @part nti__dropdown-content - The dropdown content
 */
@Component({
  tag: 'nylas-timeslot-interval',
  styleUrl: 'nylas-timeslot-interval.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasTimeslotInterval {
  @Element() host!: HTMLNylasTimeslotIntervalElement;
  /**
   * @standalone
   * The selected config
   */
  @Prop() selectedConfiguration?: Configuration;
  /**
   * @standalone
   * The name of the calendar picker.
   */
  @Prop() name: string = 'timeslot-interval';

  /**
   * @standalone
   * The event duration in minutes.
   */
  @Prop() eventDurationMinutes?: number = this.selectedConfiguration?.availability?.duration_minutes;

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * The internal event duration minutes state.
   */
  @State() internalEventDurationMinutes!: number;

  /**
   * The selected interval minutes.
   */
  @State() selectedInterval!: number;

  /**
   * The selected round to minutes.
   */
  @State() selectedRoundTo!: boolean;

  /***
   * The state to store that component is ready to render.
   */
  @State() componentLoaded: boolean = false;

  /**
   * This event is fired when the selected booking calendar changes.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-timeslot-interval', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newValue: Configuration) {
    debug('nylas-timeslot-interval', 'selectedConfigurationChangedHandler', newValue);
    this.internalEventDurationMinutes = newValue?.availability?.duration_minutes ?? this.eventDurationMinutes ?? 30;
    const internalIntervalMinutes = newValue?.availability?.interval_minutes ?? this.internalEventDurationMinutes ?? 30;
    this.selectedInterval = newValue?.availability?.interval_minutes ?? internalIntervalMinutes ?? 10;
    const roundToMinutes = newValue?.availability?.round_to || null;
    this.selectedRoundTo = roundToMinutes && roundToMinutes === internalIntervalMinutes ? true : false;
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-timeslot-interval', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-timeslot-interval', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-timeslot-interval', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-timeslot-interval', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.selectedConfigurationChangedHandler(this.selectedConfiguration);
    } else {
      this.internalEventDurationMinutes = this.eventDurationMinutes ?? 30;
      const internalIntervalMinutes = this.internalEventDurationMinutes;
      this.selectedInterval = internalIntervalMinutes ?? 10;
      this.selectedRoundTo = false;
    }
    this.componentLoaded = true;
  }

  componentWillUpdate() {
    debug('nylas-timeslot-interval', 'componentWillUpdate');
  }

  componentDidUpdate() {
    debug('nylas-timeslot-interval', 'componentDidUpdate');
  }

  componentWillRender() {
    debug('nylas-timeslot-interval', 'componentWillRender');
  }

  componentDidRender() {
    debug('nylas-timeslot-interval', 'componentDidRender');
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @Listen('nylasFormDropdownChanged')
  nylasFormDropdownChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    debug('nylas-timeslot-interval', 'nylasFormDropdownChangedHandler', event.detail);
    const value = event.detail.value;
    if (value === 'duration') {
      this.selectedInterval = this.internalEventDurationMinutes ?? 10;
    } else {
      this.selectedInterval = parseInt(event.detail.value);
    }

    this.valueChanged.emit({
      value: JSON.stringify({
        interval: this.selectedInterval,
        roundTo: this.selectedRoundTo ? this.selectedInterval : defaultRoundToMinutes,
      }),
      name: this.name,
    });
    if (typeof this.internals.setFormValue === 'function') {
      this.internals.setFormValue(JSON.stringify({ interval: this.selectedInterval, roundTo: this.selectedRoundTo ? this.selectedInterval : defaultRoundToMinutes }), this.name);
    }
  }

  @Listen('nylasFormRadioChanged')
  nylasFormRadioChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    debug('nylas-timeslot-interval', 'nylasFormRadioChangedHandler', event.detail);
    const value = event.detail.value;
    if (value === 'no-rounding') {
      this.selectedRoundTo = false;
    } else {
      this.selectedRoundTo = true;
    }

    this.valueChanged.emit({
      value: JSON.stringify({
        interval: this.selectedInterval,
        roundTo: this.selectedRoundTo ? this.selectedInterval : defaultRoundToMinutes,
      }),
      name: this.name,
    });
    if (typeof this.internals.setFormValue === 'function') {
      this.internals.setFormValue(JSON.stringify({ interval: this.selectedInterval, roundTo: this.selectedRoundTo ? this.selectedInterval : defaultRoundToMinutes }), this.name);
    }
  }

  @RegisterComponent<NylasTimeslotInterval, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-timeslot-interval',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    fireRegisterEvent: true,
  })
  render() {
    const intervalPreferenceOptions = [
      { value: 'duration', label: i18next.t('nylasTimeslotInterval.lengthOfMeeting') },
      { value: '5', label: `5 ${i18next.t('nylasTimeslotInterval.minutesShort')}` },
      { value: '10', label: `10 ${i18next.t('nylasTimeslotInterval.minutesShort')}` },
      { value: '15', label: `15 ${i18next.t('nylasTimeslotInterval.minutesShort')}` },
      { value: '20', label: `20 ${i18next.t('nylasTimeslotInterval.minutesShort')}` },
      { value: '30', label: `30 ${i18next.t('nylasTimeslotInterval.minutesShort')}` },
      { value: '45', label: `45 ${i18next.t('nylasTimeslotInterval.minutesShort')}` },
      { value: '60', label: `60 ${i18next.t('nylasTimeslotInterval.minutesShort')}` },
    ];

    const defaultSelectedInterval =
      typeof this.selectedInterval !== 'undefined'
        ? this.selectedInterval == this.internalEventDurationMinutes
          ? intervalPreferenceOptions[0]
          : intervalPreferenceOptions.find(op => op.value == this.selectedInterval.toString()) ?? intervalPreferenceOptions[0]
        : undefined;

    return (
      <Host>
        <div class="nylas-timeslot-interval" part="nti">
          <div class="header" part="nti__header">
            <h3>{i18next.t('nylasTimeslotInterval.headerTitle')}</h3>
          </div>

          <div class="nylas-timeslot-interval__dropdown">
            <span class="label" part="nti__input-label">
              {i18next.t('nylasTimeslotInterval.timeslotInterval.label')}
            </span>
            {defaultSelectedInterval?.label && (
              <select-dropdown
                name="interval-minutes"
                options={intervalPreferenceOptions}
                withSearch={false}
                exportparts="sd_dropdown: nti__dropdown, sd_dropdown-button: nti__dropdown-button, sd_dropdown-content: nti__dropdown-content"
                defaultSelectedOption={defaultSelectedInterval}
                themeConfig={this.themeConfig}
              />
            )}
            <span slot="label" class="label" part="nti__input-label">
              {i18next.t('nylasTimeslotInterval.timeslotRounding.label')}
              <tooltip-component>
                <info-icon slot="tooltip-icon" />
                <span slot="tooltip-content">{i18next.t('nylasTimeslotInterval.timeslotRounding.tooltip.desc')}</span>
              </tooltip-component>
            </span>
            {this.componentLoaded && (
              <radio-button-group
                name="interval-preference"
                defaultSelectedValue={this.selectedRoundTo ? 'rounding' : 'no-rounding'}
                options={[
                  {
                    value: 'no-rounding',
                    label: i18next.t('nylasTimeslotInterval.timeslotRoundingOptions.noRounding.label'),
                    description: i18next.t('nylasTimeslotInterval.timeslotRoundingOptions.noRounding.desc'),
                  },
                  {
                    value: 'rounding',
                    label: i18next.t('nylasTimeslotInterval.timeslotRoundingOptions.rounding.label'),
                    description: i18next.t('nylasTimeslotInterval.timeslotRoundingOptions.rounding.desc'),
                  },
                ]}
                themeConfig={this.themeConfig}
              />
            )}
          </div>
        </div>
      </Host>
    );
  }
}
